'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import { DoorOpen, ClipboardList, Shield, ChevronRight, ChevronLeft, LogOut, Bell, Settings, LayoutDashboard, Lock, Package, Factory } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const navItems: NavItem[] = [
  {
    id: 'nav-gate',
    label: 'شاشة البوابة',
    href: '/',
    icon: <DoorOpen size={20} />,
  },
  {
    id: 'nav-inside',
    label: 'الموجودون الآن',
    href: '/currently-inside-dashboard',
    icon: <LayoutDashboard size={20} />,
    badge: 6,
  },
  {
    id: 'nav-log',
    label: 'سجل الزوار',
    href: '/visitors-log',
    icon: <ClipboardList size={20} />,
  },
  {
    id: 'nav-security',
    label: 'أفراد الأمن والورديات',
    href: '/security-management',
    icon: <Shield size={20} />,
  },
  {
    id: 'nav-permissions',
    label: 'الصلاحيات والأدوار',
    href: '/permissions',
    icon: <Lock size={20} />,
  },
  {
    id: 'nav-raw-materials',
    label: 'المواد الخام',
    href: '/raw-materials',
    icon: <Package size={20} />,
  },
  {
    id: 'nav-finished-products',
    label: 'المنتج التام',
    href: '/finished-products',
    icon: <Factory size={20} />,
  },
];

const bottomItems: NavItem[] = [
  {
    id: 'nav-alerts',
    label: 'التنبيهات',
    href: '#',
    icon: <Bell size={20} />,
    badge: 3,
  },
  {
    id: 'nav-settings',
    label: 'الإعدادات',
    href: '#',
    icon: <Settings size={20} />,
  },
];

interface SidebarProps {
  activeRoute: string;
}

export default function Sidebar({ activeRoute }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`
        relative flex flex-col bg-card border-l border-border
        transition-all duration-300 ease-in-out shrink-0
        ${collapsed ? 'w-16' : 'w-64'}
      `}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 p-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <div className="flex items-center gap-2">
          <AppLogo size={36} />
          {!collapsed && (
            <div>
              <span className="font-bold text-sm text-foreground block leading-tight">VisitorGate</span>
              <span className="text-xs text-muted-foreground">نظام البوابة</span>
            </div>
          )}
        </div>
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -left-3 top-16 z-10 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-accent hover:border-accent transition-all duration-150"
        aria-label={collapsed ? 'توسيع الشريط الجانبي' : 'طي الشريط الجانبي'}
        suppressHydrationWarning
      >
        {collapsed ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
      </button>

      {/* Current user */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 p-3 rounded-lg bg-muted/50 border border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              <Shield size={14} className="text-accent" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">عمر سالم</p>
              <p className="text-xs text-muted-foreground truncate">أمن البوابة — الوردية الصباحية</p>
            </div>
          </div>
        </div>
      )}

      {/* Nav section label */}
      {!collapsed && (
        <p className="px-4 pt-4 pb-1 text-xs font-semibold text-muted-foreground uppercase tracking-widest">
          الوحدات
        </p>
      )}

      {/* Main nav */}
      <nav className="flex-1 px-2 py-1 space-y-0.5 overflow-y-auto scrollbar-thin">
        {navItems.map((item) => {
          const isActive = activeRoute === item.href;
          return (
            <Link
              key={item.id}
              href={item.href}
              className={`
                flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-150 group relative
                ${isActive
                  ? 'bg-accent/15 text-accent border border-accent/30' :'text-secondary-foreground hover:bg-muted hover:text-foreground'
                }
                ${collapsed ? 'justify-center' : ''}
              `}
              title={collapsed ? item.label : undefined}
            >
              <span className={`shrink-0 ${isActive ? 'text-accent' : ''}`}>
                {item.icon}
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge !== undefined && (
                    <span className="bg-accent text-accent-foreground text-xs font-bold px-1.5 py-0.5 rounded-full tabular-nums">
                      {item.badge}
                    </span>
                  )}
                </>
              )}
              {collapsed && item.badge !== undefined && (
                <span className="absolute top-1 left-1 w-4 h-4 bg-accent text-accent-foreground text-xs font-bold rounded-full flex items-center justify-center tabular-nums">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom nav */}
      <div className="px-2 py-2 border-t border-border space-y-0.5">
        {bottomItems.map((item) => (
          <Link
            key={item.id}
            href={item.href}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
              text-secondary-foreground hover:bg-muted hover:text-foreground
              transition-all duration-150 relative
              ${collapsed ? 'justify-center' : ''}
            `}
            title={collapsed ? item.label : undefined}
          >
            <span className="shrink-0">{item.icon}</span>
            {!collapsed && (
              <>
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge !== undefined && (
                  <span className="bg-warning text-warning-foreground text-xs font-bold px-1.5 py-0.5 rounded-full tabular-nums">
                    {item.badge}
                  </span>
                )}
              </>
            )}
            {collapsed && item.badge !== undefined && (
              <span className="absolute top-1 left-1 w-4 h-4 bg-warning text-warning-foreground text-xs font-bold rounded-full flex items-center justify-center tabular-nums">
                {item.badge}
              </span>
            )}
          </Link>
        ))}

        <button
          className={`
            w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
            text-danger hover:bg-danger/10 transition-all duration-150
            ${collapsed ? 'justify-center' : ''}
          `}
          title={collapsed ? 'تسجيل الخروج' : undefined}
          suppressHydrationWarning
        >
          <LogOut size={20} className="shrink-0" />
          {!collapsed && <span>تسجيل الخروج</span>}
        </button>
      </div>
    </aside>
  );
}